import { handleActions, combineActions } from 'redux-actions'
import { fromJS, Set as ImmutableSet, isImmutable } from 'immutable'

import * as Actions from 'actions/journalists'
import * as AppActions from 'actions/app'

export const initialState = fromJS({
  animateSearchBtn: false,
  contentBasedSearchAvailabilityLoaded: false,
  contentBasedSearchContentsLoading: false,
  contentBasedSearchError: null,
  contentBasedSearchSearchSucceeded: false,
  contentBasedSearchSearching: false,
  contentBasedSearchShowSelection: false,
  detailPageJournalistId: null,
  distributionListsDialogJoIds: [],
  distributionListsDialogOpen: false,
  fetchNewsRequestRunning: false,
  fetchPublicationsRequestRunning: false,
  importingJournalistsIds: [],
  importJournalistRequestRunning: false,
  iptcMediaTopicsDialogOpen: false,
  iptcMediaTopics: {
    searchQuery: '',
    searchInDefinitions: false,
    expandedIds: ImmutableSet([])
  },
  moduleReady: false,
  newDistributionLists: [],
  pristine: true,
  requestRunning: false,
  searchDlRequestRunning: false,
  selectedDistributionLists: [],
  selectedIdentityId: null,
  selectedJournalistIds: [],
  selectedResultsTab: 'new_contacts',
  showPersonFilters: true,
  showPublicationFilters: false,
  showShoppingCartWarning: false,
  showTopicHelperText: false,
  scrollToTop: false,
  scrollToJournalistId: null
})

export default handleActions({
  [Actions.fetchContentBasedSearchContentsStart]: state => state
    .set('contentBasedSearchContentsLoading', true)
    .set('contentBasedSearchError', null)
    .set('contentBasedSearchSearchSucceeded', false)
    .set('contentBasedSearchShowSelection', false),
  [Actions.fetchContentBasedSearchContentsSuccess]: (state, { payload }) => state
    .set('contentBasedSearchAvailabilityLoaded', true)
    .set('contentBasedSearchContentsLoading', false)
    .set('contentBasedSearchShowSelection', payload.showSelection)
    .set('contentBasedSearchError', null),
  [Actions.fetchContentBasedSearchContentsError]: (state, { payload }) => state
    .set('contentBasedSearchAvailabilityLoaded', true)
    .set('contentBasedSearchContentsLoading', false)
    .set('contentBasedSearchError', payload),

  [Actions.findJournalistsByContentsStart]: state => state
    .set('contentBasedSearchError', null)
    .set('contentBasedSearchSearchSucceeded', false)
    .set('contentBasedSearchSearching', true),
  [Actions.findJournalistsByContentsSuccess]: state => state
    .set('contentBasedSearchSearchSucceeded', true)
    .set('contentBasedSearchSearching', false)
    .set('contentBasedSearchShowSelection', false),
  [Actions.findJournalistsByContentsError]: (state, { payload }) => state
    .set('contentBasedSearchError', payload)
    .set('contentBasedSearchSearching', false),
  [Actions.resetContentBasedSearch]: state => state
    .set('contentBasedSearchError', null)
    .set('contentBasedSearchSearchSucceeded', false)
    .set('contentBasedSearchShowSelection', false),

  [combineActions(
    Actions.searchJournalistsStart,
    Actions.setPagination,
    Actions.setMyContactsPagination
  )]: state => state
    .set('requestRunning', true)
    .set('pristine', false)
    .set('scrollToTop', true),
  [Actions.searchJournalistsSuccess]: state => (
    state.set('requestRunning', false).set('showTopicHelperText', false)
  ),
  [Actions.searchJournalistsError]: state => state.set('requestRunning', false),

  [combineActions(
    Actions.importJournalistsStart,
    Actions.importJournalistsToListStart
  )]: state => state.set('importJournalistRequestRunning', true),

  [combineActions(
    Actions.importJournalistsSuccess,
    Actions.importJournalistsError,
    Actions.importJournalistsToListSuccess,
    Actions.importJournalistsToListError
  )]: state => state.set('importJournalistRequestRunning', false),
  [Actions.openJournalistDetails]: (state, { payload }) => state
    .set('detailPageJournalistId', payload)
    .set('scrollToJournalistId', payload),
  [Actions.closeJournalistDetails]: state => state
    .set('detailPageJournalistId', null)
    .set('selectedIdentityId', null),

  [Actions.findNewsStart]: state => state.set('fetchNewsRequestRunning', true),

  [combineActions(
    Actions.findNewsSuccess,
    Actions.findNewsError
  )]: state => state.set('fetchNewsRequestRunning', false),

  [Actions.searchDistributionListsStart]: state => state.set('searchDlRequestRunning', true),
  [combineActions(
    Actions.searchDistributionListsSuccess,
    Actions.searchDistributionListsError
  )]: state => state.set('searchDlRequestRunning', false),

  [Actions.fetchPublicationsStart]: state => state.set('fetchPublicationsRequestRunning', true),
  [combineActions(
    Actions.fetchPublicationsSuccess,
    Actions.fetchPublicationsError
  )]: state => state.set('fetchPublicationsRequestRunning', false),

  [Actions.toggleJournalistId]: (state, { payload }) => {
    const selectedJournalistIds = state.get('selectedJournalistIds')
    const index = selectedJournalistIds.indexOf(payload)

    if (index === -1) {
      return state.set('selectedJournalistIds', selectedJournalistIds.push(payload))
    }

    return state.set('selectedJournalistIds', selectedJournalistIds.delete(index))
  },
  [Actions.resetJournalistIds]: state => state.set('selectedJournalistIds', fromJS([])),

  [combineActions(
    Actions.importJournalistsSuccess,
    Actions.importJournalistsToListSuccess
  )]: state => state
    .set('selectedJournalistIds', fromJS([]))
    .set('selectedDistributionLists', fromJS([])),

  [Actions.openDistributionListsDialog]: state => state.set('distributionListsDialogOpen', true),
  [Actions.closeDistributionListsDialog]: state => state.set('distributionListsDialogOpen', false),

  [Actions.openIptcMediaTopicsDialog]: state => state.set('iptcMediaTopicsDialogOpen', true),
  [Actions.closeIptcMediaTopicsDialog]: (state, { payload }) => state
    .set('iptcMediaTopicsDialogOpen', false)
    .set('showTopicHelperText', payload),

  [Actions.setDistributionListsDialogJoIds]: (state, { payload }) => state.set('distributionListsDialogJoIds', fromJS(payload)),
  [Actions.setSelectedDistributionLists]: (state, { payload }) => state.set('selectedDistributionLists', fromJS(payload)),
  [Actions.setNewDistributionLists]: (state, { payload }) => state.set('newDistributionLists', fromJS(payload)),

  [Actions.setIptcMediaTopics]: (state, { payload }) => {
    const { key, value } = payload

    if (key === 'expandedIds') {
      return state.setIn(['iptcMediaTopics', key], ImmutableSet(value))
    }

    return state.setIn(['iptcMediaTopics', key], value)
  },

  [Actions.setAnimateSearchBtn]: (state, { payload }) => state.set('animateSearchBtn', payload),
  [Actions.setShowTopicHelperText]: (state, { payload }) => state.set('showTopicHelperText', payload),
  [Actions.setSelectedIdentity]: (state, { payload }) => state.set('selectedIdentityId', payload),
  [Actions.resetSelectedIdentity]: state => state.set('selectedIdentityId', null),

  [Actions.setImportingJournalistsIds]: (state, { payload }) => state
    .set('importingJournalistsIds', isImmutable(payload) ? payload : fromJS(payload)),
  [Actions.clearImportingJournalistsIds]: state => state.set('importingJournalistsIds', fromJS([])),

  [Actions.setScrollTo]: (state, { payload }) => state.set('scrollToJournalistId', payload),
  [Actions.clearScrollTo]: state => state.set('scrollToTop', false).set('scrollToJournalistId', null),

  [Actions.setSelectedJournalistIds]: (state, { payload }) => state.set('selectedJournalistIds', payload),
  [Actions.setSelectedResultsTab]: (state, { payload }) => state.set('selectedResultsTab', payload),
  [Actions.setShowShoppingCartWarning]: (state, { payload }) => state.set('showShoppingCartWarning', payload),
  [AppActions.resetState]: () => initialState
}, initialState)
